import { useState } from 'react';
import { Archive, RotateCcw, Save, Trash2, UserMinus } from 'lucide-react';
import { ConfirmDialog } from '@/components/dialog/ConfirmDialog';
import { FormDialog } from '@/components/dialog/FormDialog';
import { TextareaField } from '@/components/form/TextareaField';
import { TextField } from '@/components/form/TextField';
import { TeamColorField } from '@/components/teams/TeamColorField';
import { isHexColor } from '@/components/teams/teamColors';
import { useSubmit } from '@/hooks/useSubmit';
import { companyUrn } from '@/models/entities/CompanyModel';
import TeamMemberModel, { teamMemberUrn } from '@/models/entities/TeamMemberModel';
import TeamModel, { teamUrn } from '@/models/entities/TeamModel';
import { TEAM_MEMBER_STATUS, TEAM_STATUS } from '@/models/references/references';
import TeamMemberStatusHistoryModel from '@/models/services/TeamMemberStatusHistoryModel';
import TeamStatusHistoryModel from '@/models/services/TeamStatusHistoryModel';
import { resolveReference } from '@/stores/useReferenceOptions';

/** Création (team null, dans la société donnée) ou modification d'une équipe : nom, description et couleur du tag */
export function TeamFormDialog({
  team,
  companyId,
  onClose,
  onSaved,
}: {
  team: TeamModel | null;
  companyId: number;
  onClose: () => void;
  onSaved: (team: TeamModel) => void;
}) {
  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [color, setColor] = useState<string | null>(team && isHexColor(team.color) ? team.color : null);
  const { submit, submitting, error } = useSubmit();

  const save = () =>
    submit(async () => {
      const attributes = { name: name.trim(), description: description.trim() || null, color };
      const { model } = await new TeamModel({ id: team?.id }).save(
        team ? attributes : { ...attributes, company: companyUrn(companyId) },
        { silentError: true },
      );
      onSaved(model);
    });

  return (
    <FormDialog
      title={team ? `Modifier l'équipe ${team.name}` : 'Nouvelle équipe'}
      description="Le nom est unique dans la société."
      submitLabel={team ? 'Enregistrer' : 'Créer'}
      submitIcon={Save}
      canSubmit={!!name.trim()}
      submitting={submitting}
      error={error}
      onSubmit={save}
      onClose={onClose}
    >
      <TextField
        id="team-name"
        label="Nom de l'équipe"
        required
        placeholder="Ex : Marketing"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={100}
        autoFocus
      />
      <TextareaField
        id="team-description"
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={1000}
        rows={3}
      />
      <TeamColorField value={color} onChange={setColor} name={name} />
    </FormDialog>
  );
}

/** Archivage (ses membres en sortent) ou réactivation d'une équipe (POST /team_status_histories) */
export function TeamStatusDialog({
  team,
  onClose,
  onChanged,
}: {
  team: TeamModel;
  onClose: () => void;
  onChanged: () => void;
}) {
  const archiving = team.isActive;

  return (
    <ConfirmDialog
      title={`${archiving ? 'Archiver' : 'Réactiver'} l'équipe ${team.name} ?`}
      description={
        archiving
          ? "Ses membres en sortent et pourront rejoindre une autre équipe. L'équipe reste consultable avec son historique."
          : 'Elle repart sans membre : ajoutez ensuite son manager et ses équipiers.'
      }
      confirmLabel={archiving ? 'Archiver' : 'Réactiver'}
      confirmIcon={archiving ? Archive : RotateCcw}
      onConfirm={async () => {
        const status = await resolveReference('teamStatuses', archiving ? TEAM_STATUS.ARCHIVED : TEAM_STATUS.ACTIVE);
        await new TeamStatusHistoryModel().save(
          { team: teamUrn(team.id), teamStatus: status.urn },
          { silentError: true },
        );
      }}
      onDone={onChanged}
      onClose={onClose}
    />
  );
}

/** Suppression d'une équipe (DELETE /teams/{id}) : refusée par l'API si elle a déjà eu des membres (il faut l'archiver) */
export function DeleteTeamDialog({
  team,
  onClose,
  onDeleted,
}: {
  team: TeamModel;
  onClose: () => void;
  onDeleted: () => void;
}) {
  return (
    <ConfirmDialog
      title={`Supprimer l'équipe ${team.name} ?`}
      description="Possible seulement pour une équipe qui n'a jamais eu de membre (créée par erreur) ; sinon, archivez-la."
      confirmLabel="Supprimer"
      confirmIcon={Trash2}
      destructive
      onConfirm={() => new TeamModel({ id: team.id }).destroy()}
      onDone={onDeleted}
      onClose={onClose}
    />
  );
}

/** Retrait d'une personne de l'équipe (statut Inactif, historisé) : elle reste employée de la société */
export function RemoveTeamMemberDialog({
  team,
  teamMember,
  onClose,
  onRemoved,
}: {
  /** L'équipe affichée (le TeamMember n'a que son identité) */
  team: TeamModel;
  teamMember: TeamMemberModel;
  onClose: () => void;
  onRemoved: () => void;
}) {
  return (
    <ConfirmDialog
      title={`Retirer ${teamMember.fullName} de l'équipe ${team.name} ?`}
      description={`${teamMember.isManager ? "L'équipe n'aura plus de manager. " : ''}${teamMember.fullName} reste employé de la société et pourra rejoindre une autre équipe.`}
      confirmLabel="Retirer"
      confirmIcon={UserMinus}
      destructive
      onConfirm={async () => {
        const inactive = await resolveReference('teamMemberStatuses', TEAM_MEMBER_STATUS.INACTIVE);
        await new TeamMemberStatusHistoryModel().save(
          { teamMember: teamMemberUrn(teamMember.id), teamMemberStatus: inactive.urn },
          { silentError: true },
        );
      }}
      onDone={onRemoved}
      onClose={onClose}
    />
  );
}
